'use client';

import { useState, useSyncExternalStore } from 'react';
import { ChevronRight } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { isNative } from '@/lib/native/is-native';
import {
  personPoint,
  pinsFor,
  staticMapUrl,
  type TeamMapBlock,
  type TeamMapPerson,
  type TeamMapStatus,
  type TeamMapStop,
  type TeamMapStopStatus,
} from '@/lib/chat/blocks/team-map';

/**
 * "Where is everyone?" — today's work on a map, one row per person
 * (`src/lib/chat/blocks/team-map.ts`).
 *
 * 🚨 Every pin is a JOB. Nothing on this component says where a person or a van
 * is, how far away, or when they will get anywhere — the legend says so in as
 * many words, because a map reads as live tracking unless it is told not to.
 *
 * The map image is built HERE, at render, from the block's coordinates and the
 * public Mapbox token. The stored block never holds a URL or a token, so an old
 * reply redraws with whatever token the build has now; with none, the rows
 * stand on their own.
 *
 * 🚨 In the iPhone and Android apps there is NO map image and no go-row to
 * `/map`. Live Map is kept out of the native binaries on purpose
 * (`MapNativeGuard`): a map of named staff reads as employee tracking to App
 * Store review (guideline 2.5.4), and `/map` bounces home there anyway. The
 * rows — who is on what, and where the job is — are the same facts the
 * Engineers page shows on a phone, so they stay.
 */

const noSubscribe = () => () => {};
/** Native or not, hydration-safe: the server (and the first client pass) say
 *  "native", so the image only ever appears once the browser has said web. */
function useIsNative(): boolean {
  return useSyncExternalStore(noSubscribe, isNative, () => true);
}

const PILL: Record<TeamMapStatus, string> = {
  'on-site': 'On site',
  'on-the-way': 'On the way',
  'not-started': 'Not started',
  finished: 'Finished',
  'nothing-booked': 'Nothing booked',
};

const LEGEND: Record<TeamMapStopStatus, string> = {
  'on-site': 'On site',
  'on-the-way': 'On the way',
  'not-started': 'Not started',
  finished: 'Finished',
};

const LEGEND_ORDER: TeamMapStopStatus[] = ['on-site', 'on-the-way', 'not-started', 'finished'];

function displayName(p: TeamMapPerson): string {
  return p.isYou ? 'You' : p.name;
}

/** "Power flush · BS5 7ZZ" */
function jobLine(stop: TeamMapStop): string {
  return [stop.title, stop.postcode].filter(Boolean).join(' · ');
}

/**
 * The small print under a name. A time is always the diary's — when they
 * started, the booked start, when they finished — never an arrival.
 */
export function personDetail(p: TeamMapPerson): string {
  const stop = p.at == null ? null : p.stops[p.at];
  if (!stop) return 'Nothing booked today';
  const line = jobLine(stop);
  switch (p.status) {
    case 'on-site':
      return p.clock ? `${line} · since ${p.clock}` : line;
    case 'on-the-way':
      return p.clock ? `Heading to: ${line} · booked ${p.clock}` : `Heading to: ${line}`;
    case 'not-started':
      return p.clock ? `Next: ${line} · ${p.clock}` : `Next: ${line}`;
    case 'finished':
      return p.clock ? `Last: ${line} · done ${p.clock}` : `Last: ${line}`;
    default:
      return line;
  }
}

function notOnMapReason(stop: TeamMapStop): string {
  return stop.postcode ? 'Not on the map: postcode not recognised' : 'Not on the map: no postcode';
}

function countsLine(block: TeamMapBlock): string {
  const order: TeamMapStatus[] = ['on-site', 'on-the-way', 'not-started', 'finished', 'nothing-booked'];
  const parts = order
    .map(s => [s, block.people.filter(p => p.status === s).length] as const)
    .filter(([, n]) => n > 0)
    .map(([s, n]) => `${n} ${PILL[s].toLowerCase()}`);
  return parts.join(' · ');
}

export function TeamMap({ block, live, onOpen }: {
  block: TeamMapBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const initial = block.focusUserId && block.people.some(p => p.userId === block.focusUserId) ? block.focusUserId : 'all';
  const [show, setShow] = useState<string>(initial);
  const [imageFailed, setImageFailed] = useState(false);
  const native = useIsNative();

  const pins = pinsFor(block, show);
  const url = imageFailed || native ? null : staticMapUrl(pins, process.env.NEXT_PUBLIC_MAPBOX_TOKEN);
  const statuses = LEGEND_ORDER.filter(s => pins.some(p => p.status === s));
  const person = show === 'all' ? null : block.people.find(p => p.userId === show) ?? null;
  const rows = person ? [person] : block.people;
  const chips = block.people.length > 1;
  const open = block.open && !(native && block.open.path === '/map') ? block.open : null;
  const asOf = live ? `As of ${block.asOf}` : `As of ${block.asOfLabel}`;

  return (
    <div className="mchat-tmap">
      <p className="mchat-tmap-counts">{countsLine(block)}</p>

      {chips && (
        <div className="mchat-tmap-chips" role="tablist" aria-label="Show">
          <button
            type="button"
            role="tab"
            aria-selected={show === 'all'}
            className={`mchat-tmap-chip${show === 'all' ? ' mchat-tmap-chip--on' : ''}`}
            onClick={() => setShow('all')}
          >
            Everyone
          </button>
          {block.people.map(p => (
            <button
              key={p.userId}
              type="button"
              role="tab"
              aria-selected={show === p.userId}
              className={`mchat-tmap-chip${show === p.userId ? ' mchat-tmap-chip--on' : ''}`}
              onClick={() => setShow(p.userId)}
            >
              {displayName(p)}
            </button>
          ))}
        </div>
      )}

      {url && (
        <div className="mchat-tmap-frame">
          {/* eslint-disable-next-line @next/next/no-img-element -- a third-party static image, sized by its URL; next/image would proxy it */}
          <img
            className="mchat-tmap-img"
            src={url}
            width={360}
            height={220}
            alt={person
              ? `Map of ${displayName(person)}'s jobs today, ${pins.length} pinned at the job addresses`
              : `Map of today's work, ${pins.length} ${pins.length === 1 ? 'person' : 'people'} pinned at their job addresses`}
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      {imageFailed && (
        <p className="mchat-tmap-note" role="status">The map didn&apos;t load. The list below still stands.</p>
      )}

      <div className="mchat-tmap-legend">
        {statuses.map(s => (
          <span key={s} className="mchat-tmap-key">
            <span className={`mchat-tmap-swatch mchat-tmap-swatch--${s}`} aria-hidden="true" />
            {LEGEND[s]}
          </span>
        ))}
        <span className="mchat-tmap-key mchat-tmap-key--plain">Shown at each job, not live GPS · {asOf}</span>
      </div>

      <div role="list" aria-label={person ? `${displayName(person)}'s day` : `${block.people.length} people`}>
        {rows.map(p => {
          const n = block.people.indexOf(p) + 1;
          const stop = p.at == null ? null : p.stops[p.at];
          const pinned = !!personPoint(p) && p.status !== 'nothing-booked';
          const main = (
            <>
              <span
                className={`mchat-tmap-badge mchat-tmap-badge--${pinned ? p.status : 'none'}`}
                aria-hidden="true"
              >
                {pinned && !person ? n : ''}
              </span>
              <span className="mchat-row-main">
                <span className="mchat-row-title">
                  {displayName(p)}
                  {p.status !== 'nothing-booked' && (
                    <span className={`mchat-tmap-pill mchat-tmap-pill--${p.status}`}>{PILL[p.status]}</span>
                  )}
                </span>
                <span className="mchat-row-detail">{personDetail(p)}</span>
                {stop && !pinned && <span className="mchat-row-detail mchat-tmap-unpinned">{notOnMapReason(stop)}</span>}
              </span>
            </>
          );
          return (
            <div role="listitem" key={p.userId}>
              {stop ? (
                <button type="button" className="mchat-row mchat-tmap-row" onClick={() => onOpen(stop.path)}>
                  {main}
                  <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
                </button>
              ) : (
                <div className="mchat-row mchat-tmap-row mchat-tmap-row--still">{main}</div>
              )}
              {person && p.stops.length > 0 && (
                <div className="mchat-tmap-stops" role="list" aria-label={`${displayName(p)}'s jobs today`}>
                  {p.stops.map(s => (
                    <button
                      key={s.jobId}
                      type="button"
                      role="listitem"
                      className="mchat-tmap-stop"
                      onClick={() => onOpen(s.path)}
                    >
                      <span className={`mchat-tmap-badge mchat-tmap-badge--${s.lat != null ? s.status : 'none'}`} aria-hidden="true">{s.lat != null ? s.n : ''}</span>
                      <span className="mchat-tmap-stop-time">{s.booked ?? '—'}</span>
                      <span className="mchat-tmap-stop-main">
                        {jobLine(s)}
                        <span className="mchat-tmap-stop-state">
                          {LEGEND[s.status]}{s.lat == null ? ` · ${notOnMapReason(s).toLowerCase()}` : ''}
                        </span>
                      </span>
                    </button>
                  ))}
                  {p.stopsMore > 0 && (
                    <p className="mchat-tmap-note">{p.stopsMore} more {p.stopsMore === 1 ? 'job' : 'jobs'} today not shown</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!person && (block.more > 0 || block.unassigned > 0) && (
        <p className="mchat-tmap-note">
          {[
            block.more > 0 ? `${block.more} more ${block.more === 1 ? 'person' : 'people'} not shown` : null,
            block.unassigned > 0
              ? `${block.unassigned} of today's ${block.unassigned === 1 ? 'jobs has' : 'jobs have'} nobody on ${block.unassigned === 1 ? 'it' : 'them'}`
              : null,
          ].filter(Boolean).join(' · ')}
        </p>
      )}

      {open && (
        <button type="button" className="mchat-row mchat-tmap-go" onClick={() => onOpen(open.path)}>
          <span className="mchat-row-main"><span className="mchat-row-title">{open.label}</span></span>
          <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
        </button>
      )}
    </div>
  );
}

export default TeamMap;
